import React, { useState, useEffect, useRef } from 'react';
import { agreementAPI, signAPI } from '../utils/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  PenTool, 
  CheckCircle, 
  Clock, 
  Download,
  AlertTriangle,
  Loader2,
  X,
  FileSignature,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const AGREEMENT_STATUS = {
  PENDING: 'pending',
  SENT: 'sent',
  VIEWED: 'viewed',
  SIGNED: 'signed',
  COMPLETED: 'completed',
};

export default function AgreementSigning({ caseId, clientName, visaType, country, onSigned }) {
  const [agreement, setAgreement] = useState(null);
  const [signRequests, setSignRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signature, setSignature] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    if (caseId) {
      fetchAgreementData();
    }
  }, [caseId]);

  const fetchAgreementData = async () => {
    try {
      setLoading(true);
      
      // Fetch agreement and sign requests in parallel
      const [agreementRes, signReqRes] = await Promise.all([
        agreementAPI.getByCase(caseId).catch(() => ({ data: [] })),
        signAPI.getCaseRequests(caseId).catch(() => ({ data: [] })),
      ]);
      
      if (agreementRes.data && agreementRes.data.length > 0) {
        setAgreement(agreementRes.data[0]);
      }
      
      setSignRequests(signReqRes.data || []);
    } catch (error) {
      console.error('Error fetching agreement data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAgreementStatus = () => {
    // Check if any sign request is completed
    const completedRequest = signRequests.find(req => req.status === 'completed');
    if (completedRequest) return AGREEMENT_STATUS.COMPLETED;
    
    // Check if agreement is signed locally
    if (agreement?.signed) return AGREEMENT_STATUS.SIGNED;
    
    // Check for sent sign requests
    const sentRequest = signRequests.find(req => req.status === 'sent' || req.status === 'viewed');
    if (sentRequest) return sentRequest.status === 'viewed' ? AGREEMENT_STATUS.VIEWED : AGREEMENT_STATUS.SENT;
    
    return AGREEMENT_STATUS.PENDING;
  };

  const handleSignInPortal = async () => {
    if (!agreedToTerms || !signature.trim()) {
      toast.error('Please agree to the terms and provide your signature');
      return;
    }

    try {
      setSigning(true);
      
      // Sign the agreement
      await agreementAPI.sign(caseId, {
        signature: signature,
        signed_at: new Date().toISOString(),
        signed_in_portal: true
      });
      
      toast.success('Agreement signed successfully!');
      setShowSignModal(false);
      fetchAgreementData();
      
      if (onSigned) {
        onSigned();
      }
    } catch (error) {
      console.error('Error signing agreement:', error);
      toast.error('Failed to sign agreement. Please try again.');
    } finally {
      setSigning(false);
    }
  };

  // Canvas drawing functions for signature
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    
    // Convert canvas to data URL for signature
    const canvas = canvasRef.current;
    if (canvas) {
      const dataUrl = canvas.toDataURL();
      setSignature(dataUrl);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignature('');
    }
  };

  const status = getAgreementStatus();

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="agreement-signing-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-teal-600" />
            Service Agreement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Status Display */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Agreement Status</p>
                <p className="text-sm text-slate-500">{visaType} - {country}</p>
              </div>
              <div>
                {status === AGREEMENT_STATUS.COMPLETED || status === AGREEMENT_STATUS.SIGNED ? (
                  <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    Signed
                  </Badge>
                ) : status === AGREEMENT_STATUS.VIEWED ? (
                  <Badge className="bg-blue-100 text-blue-700 flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    Viewed - Awaiting Signature
                  </Badge>
                ) : status === AGREEMENT_STATUS.SENT ? (
                  <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    Sent for Signature
                  </Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-700 flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4" />
                    Pending
                  </Badge>
                )}
              </div>
            </div>

            {/* Agreement Content Preview */}
            {(status === AGREEMENT_STATUS.PENDING || status === AGREEMENT_STATUS.SENT || status === AGREEMENT_STATUS.VIEWED) && (
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Service Agreement Summary</h4>
                <div className="text-sm text-slate-600 space-y-2">
                  <p><strong>Client:</strong> {clientName}</p>
                  <p><strong>Service:</strong> {visaType} Application Assistance</p>
                  <p><strong>Destination:</strong> {country}</p>
                  <p className="mt-4 text-xs text-slate-500">
                    By signing this agreement, you acknowledge that you have read and understood all terms and conditions 
                    of the service agreement for immigration consultancy services provided by Immiways Migration Advisory Services Pvt Ltd.
                  </p>
                </div>
              </div>
            )}

            {/* Signed Agreement Info */}
            {(status === AGREEMENT_STATUS.COMPLETED || status === AGREEMENT_STATUS.SIGNED) && (
              <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-800">Agreement Signed Successfully</p>
                    <p className="text-sm text-green-600 mt-1">
                      Your service agreement has been signed. You can now proceed to upload your documents.
                    </p>
                    {agreement?.signed_at && (
                      <p className="text-xs text-green-500 mt-2">
                        Signed on: {new Date(agreement.signed_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              {status === AGREEMENT_STATUS.PENDING && (
                <Button 
                  onClick={() => setShowSignModal(true)}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="sign-agreement-btn"
                >
                  <PenTool className="h-4 w-4 mr-2" />
                  Sign Agreement
                </Button>
              )}
              
              {(status === AGREEMENT_STATUS.SENT || status === AGREEMENT_STATUS.VIEWED) && (
                <Button 
                  onClick={() => setShowSignModal(true)}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="sign-agreement-btn"
                >
                  <PenTool className="h-4 w-4 mr-2" />
                  Sign Now
                </Button>
              )}

              {(status === AGREEMENT_STATUS.COMPLETED || status === AGREEMENT_STATUS.SIGNED) && signRequests.length > 0 && (
                <Button 
                  variant="outline"
                  onClick={() => {
                    // Trigger download
                    const completedReq = signRequests.find(r => r.status === 'completed');
                    if (completedReq) {
                      window.open(`/api/sign-requests/${completedReq.zoho_request_id}/download`, '_blank');
                    }
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Signed Copy
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sign Modal */}
      {showSignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FileSignature className="h-6 w-6 text-teal-600" />
                Sign Service Agreement
              </h2>
              <button 
                onClick={() => setShowSignModal(false)}
                className="p-2 hover:bg-slate-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Agreement Terms */}
              <div className="bg-slate-50 rounded-lg p-4 max-h-64 overflow-y-auto text-sm">
                <h3 className="font-semibold mb-3">Service Agreement Terms</h3>
                <div className="space-y-3 text-slate-600">
                  <p><strong>1. Scope of Services</strong></p>
                  <p>Immiways Migration Advisory Services Pvt Ltd will provide professional consultation and assistance for your {visaType} application to {country}, including:</p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Initial consultation and eligibility assessment</li>
                    <li>Document checklist preparation and guidance</li>
                    <li>Application form filling assistance</li>
                    <li>Document review and verification</li>
                    <li>Application submission guidance</li>
                    <li>Status tracking and updates</li>
                  </ul>
                  
                  <p><strong>2. Client Responsibilities</strong></p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Provide accurate and complete information</li>
                    <li>Submit all required documents within stipulated timeframe</li>
                    <li>Respond promptly to queries from the case officer</li>
                    <li>Pay all applicable fees as per agreement</li>
                  </ul>
                  
                  <p><strong>3. Important Disclaimers</strong></p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Processing times are estimates and may vary</li>
                    <li>Visa approval is at the discretion of immigration authorities</li>
                    <li>Service fees are non-refundable once processing begins</li>
                    <li>Government fees are not included in service fees</li>
                  </ul>
                </div>
              </div>

              {/* Signature Section */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Your Signature
                </label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
                  <canvas
                    ref={canvasRef}
                    width={500}
                    height={150}
                    className="w-full cursor-crosshair bg-slate-50 rounded"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                </div>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-slate-500">Draw your signature above</p>
                  <button 
                    onClick={clearCanvas}
                    className="text-sm text-teal-600 hover:text-teal-700"
                  >
                    Clear Signature
                  </button>
                </div>
              </div>

              {/* Type Signature Option */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Or type your full name as signature
                </label>
                <input
                  type="text"
                  value={signature.startsWith('data:') ? '' : signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder={clientName}
                  className="w-full px-4 py-3 border rounded-lg font-signature text-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  style={{ fontFamily: "'Brush Script MT', cursive" }}
                />
              </div>

              {/* Agreement Checkbox */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="agree-terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500"
                />
                <label htmlFor="agree-terms" className="text-sm text-slate-600">
                  I, <strong>{clientName}</strong>, confirm that I have read, understood, and agree to all the terms 
                  and conditions of this service agreement. I understand that this electronic signature has the 
                  same legal validity as a handwritten signature.
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowSignModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSignInPortal}
                  disabled={!agreedToTerms || !signature.trim() || signing}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {signing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Signing...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Sign Agreement
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
